import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { storeApi, useStore } from "./StoreContext";
import { useDocumentTitle } from "./StorefrontLayout";
import { ShoppingBag, ChevronRight, MapPin, Scissors, PawPrint, ArrowRight } from "lucide-react";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function Home() {
  useDocumentTitle("Pet grooming and essentials in one roof", "Shop premium pet food, treats, toys and book grooming or boarding for your pet — all in one place at Furmigo.");
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newest, setNewest] = useState([]);
  const [popular, setPopular] = useState([]);
  const { addToCart } = useStore();

  useEffect(() => {
    storeApi.banners("hero").then(setBanners).catch(() => {});
    storeApi.categories().then(setCategories).catch(() => {});
    storeApi.products({ sort: "newest", page_size: 12 }).then(d => setNewest(d.items)).catch(() => {});
    storeApi.products({ sort: "popular", page_size: 12 }).then(d => setPopular(d.items)).catch(() => {});
  }, []);

  return (
    <div data-testid="store-home">
      {/* Hero */}
      <section className="px-4 sm:px-6 pt-4 sm:pt-8 max-w-7xl mx-auto">
        {banners.length > 0 ? (
          <BannerCarousel banners={banners} />
        ) : (
          <DefaultHero />
        )}
      </section>

      {/* Quick category chips */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-6">
        <div className="flex overflow-x-auto gap-2 pb-2 snap-x" data-testid="category-chips" style={{ scrollbarWidth: "none" }}>
          {categories.slice(0, 12).map(c => (
            <Link key={c.id} to={`/products?category=${encodeURIComponent(c.name)}`}
              className="shrink-0 snap-start rounded-full px-4 py-2 bg-white border border-stone-200 text-sm font-semibold hover:border-amber-400 hover:text-amber-700 transition" data-testid={`chip-${c.id}`}>
              {c.name}
            </Link>
          ))}
          {categories.length === 0 && <div className="text-sm text-stone-400">Categories will appear here.</div>}
        </div>
      </section>

      <SectionRail title="Newest in store" link="/products?sort=newest" products={newest} addToCart={addToCart} testid="rail-newest" />
      <PromoCards />
      <SectionRail title="Popular picks" link="/products?sort=popular" products={popular} addToCart={addToCart} testid="rail-popular" />
    </div>
  );
}

function DefaultHero() {
  return (
    <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-amber-100 via-orange-50 to-yellow-50 border border-amber-200/60 px-6 sm:px-12 py-10 sm:py-16 relative" data-testid="hero-default">
      <div className="max-w-2xl">
        <div className="text-xs uppercase tracking-[0.25em] text-amber-700 font-semibold">Furmigo</div>
        <h1 className="font-heading text-3xl sm:text-5xl font-black mt-3 leading-tight">Pet grooming and essentials in one roof.</h1>
        <p className="text-stone-600 mt-3 text-sm sm:text-base max-w-xl">Premium food, accessories and care — plus on-demand grooming and boarding at our stores near you.</p>
        <div className="mt-5 flex gap-2 flex-wrap">
          <Link to="/products" className="inline-flex items-center gap-1.5 bg-stone-900 text-white px-5 py-3 rounded-full text-sm font-semibold hover:bg-stone-800" data-testid="hero-cta-shop">
            <ShoppingBag className="w-4 h-4" />Shop now
          </Link>
          <Link to="/book" className="inline-flex items-center gap-1.5 bg-white border border-stone-300 px-5 py-3 rounded-full text-sm font-semibold hover:bg-stone-50">
            <Scissors className="w-4 h-4" />Book grooming
          </Link>
        </div>
      </div>
      <PawPrint className="hidden sm:block w-32 h-32 absolute right-6 bottom-2 text-amber-300 opacity-60" />
    </div>
  );
}

function BannerCarousel({ banners }) {
  return (
    <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory" data-testid="banner-carousel" style={{ scrollbarWidth: "none" }}>
      {banners.map(b => (
        <a key={b.id} href={b.cta_link || "/products"}
           className="shrink-0 w-full snap-start rounded-3xl overflow-hidden relative bg-stone-200 aspect-[16/7] sm:aspect-[16/6]"
           data-testid={`banner-${b.id}`}>
          <img src={b.image_url} alt={b.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-stone-900/70 to-transparent p-6 sm:p-10 flex flex-col justify-end">
            <h2 className="font-heading text-2xl sm:text-4xl font-black text-white">{b.title}</h2>
            {b.subtitle && <p className="text-white/90 text-sm sm:text-base mt-1">{b.subtitle}</p>}
            {b.cta_label && (
              <span className="mt-3 inline-flex items-center gap-1 bg-white text-stone-900 px-4 py-2 rounded-full text-sm font-semibold w-fit">{b.cta_label} <ArrowRight className="w-4 h-4" /></span>
            )}
          </div>
        </a>
      ))}
    </div>
  );
}

function SectionRail({ title, link, products, addToCart, testid }) {
  if (!products || products.length === 0) return null;
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-8" data-testid={testid}>
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{title}</h2>
        <Link to={link} className="text-sm font-semibold text-amber-700 hover:underline flex items-center gap-1">View all <ChevronRight className="w-4 h-4" /></Link>
      </div>
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2" style={{ scrollbarWidth: "none" }}>
        {products.map(p => (
          <ProductCardMini key={p.id} p={p} onAdd={() => addToCart(p, 1)} />
        ))}
      </div>
    </section>
  );
}

export function ProductCardMini({ p, onAdd }) {
  const img = p.image_path ? `${process.env.REACT_APP_BACKEND_URL}/api/files-public/${p.image_path}` : null;
  return (
    <div className="shrink-0 snap-start w-44 sm:w-52" data-testid={`product-card-${p.id}`}>
      <Link to={`/products/${p.id}`} className="block">
        <div className="aspect-square rounded-2xl bg-stone-100 overflow-hidden border border-stone-200">
          {img ? <img src={img} alt={p.name} className="w-full h-full object-cover" loading="lazy" /> :
            <div className="w-full h-full grid place-items-center text-stone-400"><PawPrint className="w-10 h-10" /></div>}
        </div>
      </Link>
      <Link to={`/products/${p.id}`} className="block mt-2">
        <div className="text-xs text-stone-500 truncate">{p.brand || ""}</div>
        <div className="font-semibold text-sm leading-tight line-clamp-2 min-h-[2.5em]">{p.name}</div>
      </Link>
      <div className="mt-1 flex items-center justify-between gap-2">
        <div className="font-heading font-bold">{inr(p.sale_price)}</div>
        <button
          onClick={onAdd}
          disabled={!p.in_stock}
          className="rounded-full bg-amber-600 hover:bg-amber-700 disabled:bg-stone-300 text-white text-xs font-bold px-3 py-1.5"
          data-testid={`add-to-cart-${p.id}`}
        >
          {p.in_stock ? "Add" : "Sold out"}
        </button>
      </div>
    </div>
  );
}

function PromoCards() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 mt-8 grid sm:grid-cols-2 gap-3">
      <PromoCard to="/book" title="On-the-spot grooming" subtitle="Walk-ins welcome at our stores" tone="amber" icon={<Scissors />} />
      <PromoCard to="/stores" title="Find a Furmigo near you" subtitle="3 stores and counting" tone="stone" icon={<MapPin />} />
    </section>
  );
}
const PromoCard = ({ to, title, subtitle, tone, icon }) => {
  const t = { amber: "bg-amber-100 text-amber-900 border-amber-200", stone: "bg-stone-900 text-white border-stone-900" }[tone];
  return (
    <Link to={to} className={`block rounded-3xl border ${t} p-5 sm:p-6 hover:opacity-95 transition`}>
      <div className="w-10 h-10 rounded-full bg-white/80 text-stone-900 grid place-items-center mb-3">{icon}</div>
      <div className="font-heading text-xl font-bold">{title}</div>
      <div className="text-sm opacity-80">{subtitle}</div>
    </Link>
  );
};
